// src/utils/web3.js

import Web3 from 'web3';
import { CONTRACTS } from '../config/constants';

const BN = Web3.utils.toBN;

// Примерный ABI, используемый для вызовов getAmountsOut, swapExactTokensForTokens, swapExactETHForTokens и т.д.
// Расширяйте, если понадобятся дополнительные методы.
const ROUTER_ABI = [
  {
    inputs: [
      { name: 'amountIn', type: 'uint256' },
      { name: 'amountOutMin', type: 'uint256' },
      { name: 'path', type: 'address[]' },
      { name: 'to', type: 'address' },
      { name: 'deadline', type: 'uint256' }
    ],
    name: 'swapExactTokensForTokens',
    outputs: [{ name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'amountOutMin', type: 'uint256' },
      { name: 'path', type: 'address[]' },
      { name: 'to', type: 'address' },
      { name: 'deadline', type: 'uint256' }
    ],
    name: 'swapExactETHForTokens',
    outputs: [{ name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'amountIn', type: 'uint256' },
      { name: 'path', type: 'address[]' },
    ],
    name: 'getAmountsOut',
    outputs: [{ name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'view',
    type: 'function',
  },
];

// Получаем экземпляр контракта роутера SushiSwap V2
const getRouterContract = (web3) => {
  return new web3.eth.Contract(ROUTER_ABI, CONTRACTS.SUSHISWAP_V2_ROUTER);
};

// Проверяем, что адрес валиден (Ethereum-формат)
export const isValidAddress = (address) => {
  return Web3.utils.isAddress(address);
};

/**
 * Функция для получения котировки (возвращает amountOut во Wei).
 * Принимает web3, объекты tokenIn/tokenOut (с их address и т.д.) и amountInWei (количество входных токенов в Wei/units).
 */
export const getQuote = async (web3, tokenIn, tokenOut, amountInWei) => {
  const router = getRouterContract(web3);

  // Формируем path: если токен нативный (ETH), то это WETH, иначе сам токен.
  // Аналогично для выходного токена.
  const path = [
    tokenIn.isNative ? CONTRACTS.WETH : tokenIn.address,
    tokenOut.isNative ? CONTRACTS.WETH : tokenOut.address,
  ];

  try {
    const amounts = await router.methods.getAmountsOut(amountInWei, path).call();
    // Возвращаем количество выходных токенов (amountOut) в Wei
    return amounts[1];
  } catch (error) {
    console.error('getQuote error:', error);
    throw error;
  }
};

/**
 * Выполнение свопа (token->token или ETH->token) с заменой адреса получателя.
 */
export const executeSwap = async (web3, params) => {
  const { tokenIn, tokenOut, amountInWei, amountOutMinWei, account, recipient } = params;

  if (!recipient || !isValidAddress(recipient)) {
    throw new Error('Valid recipient address is required');
  }

  const router = getRouterContract(web3);
  const deadline = Math.floor(Date.now() / 1000) + 1200; // 20 минут с текущего момента

  // Формируем path
  const path = [
    tokenIn.isNative ? CONTRACTS.WETH : tokenIn.address,
    tokenOut.isNative ? CONTRACTS.WETH : tokenOut.address,
  ];

  try {
    // Если входящий токен — ETH (native)
    if (tokenIn.isNative) {
      return await router.methods
        .swapExactETHForTokens(
          amountOutMinWei,
          path,
          recipient,
          deadline
        )
        .send({
          from: account,
          value: amountInWei,
        });
    } else {
      // Иначе своп token -> token
      return await router.methods
        .swapExactTokensForTokens(
          amountInWei,
          amountOutMinWei,
          path,
          recipient,
          deadline
        )
        .send({ from: account });
    }
  } catch (error) {
    console.error('Swap execution error:', error);
    throw error;
  }
};

/**
 * Функция расчёта минимального выхода с учётом проскальзывания (в Wei).
 * @param {string} outputWei - Количество выходного токена в Wei
 * @param {number} slippagePercentage - Проскальзывание в %
 * @returns {string} Минимальное количество выхода в Wei (BN -> string)
 */
export const calculateMinimumOutput = (outputWei, slippagePercentage = 0.5) => {
  const outBN = BN(outputWei || '0');
  const factor = 1 - slippagePercentage / 100;

  // Умножаем outBN на factor (учитывая BN)
  // Для точности умножаем на 10000, чтобы избежать дробной части, потом делим обратно
  const scaled = outBN.mul(BN(Math.floor(factor * 10000))).div(BN(10000));
  return scaled.toString();
};

/**
 * Получение цены токена в USDC (за 1 единицу token).
 * Если нет ликвидности или возникает ошибка — возвращаем 0.
 */
export const getTokenPriceInUSDC = async (web3, token, usdcAddress) => {
  // Если токен уже USDC, цена = 1
  if (token.symbol === 'USDC') {
    return 1;
  }

  const router = getRouterContract(web3);

  // Считаем "1 токен" с учётом его decimals
  const oneUnitBN = BN(10).pow(BN(token.decimals));
  const oneUnit = oneUnitBN.toString();

  // Формируем путь:
  // - Для нативного ETH => [WETH, USDC]
  // - Иначе [token.address, WETH, USDC]
  let path;
  if (token.isNative) {
    path = [CONTRACTS.WETH, usdcAddress];
  } else {
    path = [token.address, CONTRACTS.WETH, usdcAddress];
  }

  try {
    // Запрашиваем у роутера getAmountsOut для 1 токена
    const amounts = await router.methods.getAmountsOut(oneUnit, path).call();

    // amounts[amounts.length - 1] - это количество USDC, которое мы получим
    const amountOutUSDCWei = amounts[amounts.length - 1];

    // USDC имеет 6 decimals, делим на 1e6
    const priceInUsdc = parseFloat(amountOutUSDCWei) / 1e6;

    return priceInUsdc; // Например 123.4567
  } catch (err) {
    console.error('getTokenPriceInUSDC error:', err);
    return 0;
  }
};
